import {
  MultiEmailInputV2,
  CustomButton,
  CustomSelect,
  ErrorMessage,
  CustomInput,
} from '@/shared/ui/components';
import { OrangeWarningIcon, trashBoxRedIcon } from '@/shared/ui/assets';
import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { zeroPad } from '@/shared/lib';
import { formatPatientAge, FOLLOW_ON_STUDY_TYPE } from '@/entities/study';
import { LoadingOutlined } from '@ant-design/icons';
import { Select, Spin } from 'antd';
import _ from 'lodash';
import { t } from 'ttag';
import { useChangeStudyType } from './useChangeStudyType';
import SEND_REQUEST from '../submitARequest.constants';
import type { ChangeStudyTypeProps } from './changeStudyType.types';
import { NEW_STUDY_TYPE_OPTIONS } from '../handler';
import './changeStudyType.scss';

const ChangeStudyType = ({
  category,
  selectedFacility,
  setLoadingPage,
  backToPrevious,
}: ChangeStudyTypeProps) => {
  const {
    searchTypeList,
    dataSearch,
    state,
    handleRemoveClick,
    onClickSendMessage,
    onChange,
    onChangeEmailList,
    onInputChange,
    onChangeSelection,
    handleSearch,
    handleChange,
  } = useChangeStudyType({ category, selectedFacility, setLoadingPage, backToPrevious });

  /**
   * Renders one study selected for a change-study-type support request with its old and new type pickers. Shows patient name, gender initial and formatted age with a trash icon calling handleRemoveClick, the zero-padded study id and start and end dates falling back to a double hyphen bullet, a disabled current type select and a new type select excluding the current type that reports the row error, plus warnings when either type is the follow-on study type.
   *
   * @param item - Selected study entry whose value holds the patient, study and type fields.
   * @param index - Position of the entry in the selected list, used for removal and error lookup.
   * @returns Fragment holding the study card and any follow-on study warnings.
   */
  const renderSelectedStudy = (item: any, index: any) => (
    <>
      <div className="selected-study-type" key={index}>
        <div className="infor-header">
          <div className="patientName">
            {`${item.value?.patientName} (${item.value?.patientGender} - ${formatPatientAge(item.value?.patientAge)})`}
          </div>
          <img
            aria-hidden
            className="delete-button"
            src={trashBoxRedIcon}
            alt={t`Delete icon`}
            onClick={() => {
              handleRemoveClick(index);
            }}
          />
        </div>

        <div className="info-container">
          <div className="studyId">
            <div className="name">{t`Study ID`}</div>
            <div className="value">{zeroPad(item.value?.studyFid)}</div>
          </div>

          <div className="border-line" />

          <div className="startDate">
            <div className="name">{t`Start Date`}</div>
            <div className="value">
              {!_.isEmpty(item.value?.startDate) ? item.value?.startDate : DOUBLE_HYPHEN_BULLET}
            </div>
          </div>

          <div className="border-line" />

          <div className="endDate">
            <div className="name">{t`End Date`}</div>
            <div className="value">
              {!_.isEmpty(item.value?.endDate) ? item.value?.endDate : DOUBLE_HYPHEN_BULLET}
            </div>
          </div>
        </div>

        <div className="input-container">
          <div className="input-container--left">
            <span className="title-select-input">{t`Current study type`}</span>
            <CustomSelect
              className="current-study-type-select-input"
              name="currentStudyType"
              value={item.value?.studyType}
              options={NEW_STUDY_TYPE_OPTIONS}
              onChange={onChange}
              size="middle"
              showSearch
              allowClear={false}
              disabled
              aria-label={t`Current study type`}
            />
          </div>
          <div className="input-container--right">
            <span className="title-select-input">{t`New study type`}</span>
            <CustomSelect
              className="new-study-type-select-input"
              name="newStudyType"
              placeholder="Select new study type"
              onChange={(name, value, option) => {
                onChangeSelection(name, option, index);
              }}
              value={item.value?.newStudyType}
              options={_.filter(
                NEW_STUDY_TYPE_OPTIONS,
                ({ value }) => value !== item.value?.studyType?.value,
              )}
              size="middle"
              showSearch
              allowClear={false}
              isError={!_.isEmpty(state.errorObject.selectedArray?.[index])}
              errorMessage={state.errorObject.selectedArray?.[index]}
              aria-label={t`New study type`}
            />
          </div>
        </div>
      </div>
      {item.value?.newStudyType?.value === FOLLOW_ON_STUDY_TYPE && (
        <div className="sub-message">
          <img src={OrangeWarningIcon} alt="Warning Icon" />
          <span>
            {t`Please make sure to specify the details of the follow-on study in your message.`}
          </span>
        </div>
      )}
      {item.value?.studyType?.value === FOLLOW_ON_STUDY_TYPE && (
        <div className="sub-message">
          <img src={OrangeWarningIcon} alt="Warning Icon" />
          <span>{t`Please note that the follow-on study will be removed.`}</span>
        </div>
      )}
    </>
  );

  /**
   * Builds the dropdown placeholder shown when a study search yields nothing. Renders a spinner with Searching text while state.isSearching is true and otherwise a no results match your search label.
   *
   * @returns Searching spinner or empty-results label element.
   */
  const renderNotFoundData = () =>
    state.isSearching ? (
      <div className="searching">
        <span role="status" aria-label={t`Loading`}>
          <Spin indicator={<LoadingOutlined spin style={{ fontSize: 24 }} />} />
        </span>
        <span className="searching-text">{t`Searching...`}</span>
      </div>
    ) : (
      <span className="no-results-found">{t`No results match your search`}</span>
    );

  return (
    <>
      <span className="big-title">{t`Request information`}</span>
      <span className="small-title">{t`Search for study`}</span>
      <div id="search-for-study-id" className="select-search-box">
        <Select
          value={null}
          searchValue={state.inputValue}
          className="custom-search-select"
          popupClassName="custom-menu-options"
          showSearch
          placeholder={`${t`Search`}...`}
          defaultActiveFirstOption={false}
          showArrow={false}
          filterOption={false}
          onSearch={handleSearch}
          onChange={handleChange}
          options={(dataSearch || []).map((d, index) => ({
            value: index,
            label: d.label,
          }))}
          popupMatchSelectWidth={false}
          placement="bottomLeft"
          notFoundContent={renderNotFoundData()}
          getPopupContainer={(ele) => ele.parentNode}
          status={!_.isEmpty(state.errorObject.searchStudy) ? 'error' : ''}
          aria-label={t`Search for study`}
        />
        <CustomSelect
          className="option-search-for-study"
          onChange={onChange}
          name="searchType"
          value={state.searchType}
          options={searchTypeList}
          size="middle"
          showSearch
          allowClear={false}
          aria-label={t`Search type`}
        />
      </div>
      {state.errorObject.searchStudy && (
        <ErrorMessage errorMessage={state.errorObject.searchStudy} hasIcon />
      )}

      {_.map(state.selectedArray, (item, index) => renderSelectedStudy(item, index))}

      <div className="message-box">
        <span className="small-title">{t`Message`}</span>
        <CustomInput
          className="physician-interpretation"
          placeholder={t`Enter your message`}
          type="textarea"
          value={state.message}
          onChange={onChange}
          name="message"
          aria-label={t`Message`}
          allowClear={false}
          isError={!_.isEmpty(state.errorObject.message)}
          errorMessage={state.errorObject.message}
        />
      </div>

      <div className="email-box">
        <span className="big-title">{t`Notification email`}</span>
        <MultiEmailInputV2
          emailList={state.emailList}
          emailInputValue={state.emailInputValue}
          onChangeEmailList={onChangeEmailList}
          onInputChange={onInputChange}
          isError={!_.isEmpty(state.errorObject.invalidEmails)}
          errorMessage={state.errorObject.invalidEmails}
        />
      </div>

      <div className="bottom-button-row">
        <CustomButton
          id="submit-btn"
          className="button-submit"
          disabled={state.isSaving}
          onClick={() => {
            setTimeout(onClickSendMessage, 100);
          }}
        >
          {SEND_REQUEST}
        </CustomButton>
      </div>
    </>
  );
};

export default ChangeStudyType;
